import React from 'react';
interface RecruitingRetentionMetricsProps {
  estimatedHires: number;
  estimatedLaunched: number;
  estimatedActive30: number;
  estimatedActive90: number;
  estimatedActive1Year: number;
}
export function RecruitingRetentionMetrics({
  estimatedHires,
  estimatedLaunched,
  estimatedActive30,
  estimatedActive90,
  estimatedActive1Year
}: RecruitingRetentionMetricsProps) {
  // Calculate the full recruiting funnel backwards from hires
  // Using the same default rates as RetentionBreakdown.tsx
  const rates = {
    offeredToHired: 85,
    interviewedToOffered: 55,
    scheduledToInterviewed: 65,
    qualifiedToScheduled: 70,
    screenedToQualified: 60,
    approvedToScreened: 50,
    applicationsToApproved: 50
  };
  // Calculate backwards from estimatedHires
  const applications = Math.round(estimatedHires / (rates.offeredToHired / 100) / (rates.interviewedToOffered / 100) / (rates.scheduledToInterviewed / 100) / (rates.qualifiedToScheduled / 100) / (rates.screenedToQualified / 100) / (rates.approvedToScreened / 100) / (rates.applicationsToApproved / 100));
  const approved = Math.round(applications * (rates.applicationsToApproved / 100));
  const screened = Math.round(approved * (rates.approvedToScreened / 100));
  const qualified = Math.round(screened * (rates.screenedToQualified / 100));
  const scheduled = Math.round(qualified * (rates.qualifiedToScheduled / 100));
  const interviewed = Math.round(scheduled * (rates.scheduledToInterviewed / 100));
  const offered = Math.round(interviewed * (rates.interviewedToOffered / 100));
  const maxRecruitingValue = applications;
  return <div className="space-y-6" data-id="element-409">
      {/* Recruiting Funnel */}
      <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8" data-id="element-410">
        <h3 className="text-lg md:text-xl font-bold text-navy mb-2" data-id="element-411">
          Recruiting Metrics
        </h3>
        <p className="text-sm text-gray-600 mb-6" data-id="element-412">
          Touch and drag the percentages up/down to adjust
        </p>

        <div className="space-y-3" data-id="element-413">
          {/* Applications */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-414">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-415">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-416">
                {applications}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-417">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-418">
                Applications
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-419">
              <div className="h-full bg-gradient-to-r from-gray-300 to-gray-200 w-full transition-all duration-500 ease-out" data-id="element-420" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-421">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-422">
                {rates.applicationsToApproved}%
              </span>
            </div>
          </div>

          {/* Approved */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-423">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-424">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-425">
                {approved}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-426">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-427">
                Approved
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-428">
              <div className="h-full bg-gradient-to-r from-gray-400 to-gray-300 transition-all duration-500 ease-out" style={{
              width: `${approved / maxRecruitingValue * 100}%`
            }} data-id="element-429" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-430">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-431">
                {rates.approvedToScreened}%
              </span>
            </div>
          </div>

          {/* Screened */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-432">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-433">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-434">
                {screened}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-435">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-436">
                Screened
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-437">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-500 ease-out" style={{
              width: `${screened / maxRecruitingValue * 100}%`
            }} data-id="element-438" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-439">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-440">
                {rates.screenedToQualified}%
              </span>
            </div>
          </div>

          {/* Qualified */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-441">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-442">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-443">
                {qualified}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-444">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-445">
                Qualified
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-446">
              <div className="h-full bg-gradient-to-r from-indigo-400 to-indigo-300 transition-all duration-500 ease-out" style={{
              width: `${qualified / maxRecruitingValue * 100}%`
            }} data-id="element-447" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-448">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-449">
                {rates.qualifiedToScheduled}%
              </span>
            </div>
          </div>

          {/* Scheduled */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-450">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-451">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-452">
                {scheduled}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-453">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-454">
                Scheduled
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-455">
              <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 transition-all duration-500 ease-out" style={{
              width: `${scheduled / maxRecruitingValue * 100}%`
            }} data-id="element-456" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-457">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-458">
                {rates.scheduledToInterviewed}%
              </span>
            </div>
          </div>

          {/* Interviewed */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-459">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-460">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-461">
                {interviewed}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-462">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-463">
                Interviewed
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-464">
              <div className="h-full bg-gradient-to-r from-purple-400 to-purple-300 transition-all duration-500 ease-out" style={{
              width: `${interviewed / maxRecruitingValue * 100}%`
            }} data-id="element-465" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-466">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-467">
                {rates.interviewedToOffered}%
              </span>
            </div>
          </div>

          {/* Offered */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-468">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-469">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-470">
                {offered}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-471">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-472">
                Offered
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-473">
              <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500 ease-out" style={{
              width: `${offered / maxRecruitingValue * 100}%`
            }} data-id="element-474" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-475">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-476">
                {rates.offeredToHired}%
              </span>
            </div>
          </div>

          {/* Hired */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-477">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-478">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-479">
                {estimatedHires}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-480">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-481">
                Hired
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-482">
              <div className="h-full bg-gradient-to-r from-navy to-navy/90 transition-all duration-500 ease-out" style={{
              width: `${estimatedHires / maxRecruitingValue * 100}%`
            }} data-id="element-483" />
            </div>
            <div className="w-10 md:w-16 flex-shrink-0" data-id="element-484" />
          </div>
        </div>
      </div>

      {/* Retention Metrics */}
      <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8" data-id="element-485">
        <h3 className="text-lg md:text-xl font-bold text-navy mb-6" data-id="element-486">
          Retention Metrics
        </h3>

        <div className="space-y-3" data-id="element-487">
          {/* Hired (starting point) */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-488">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-489">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-490">
                {estimatedHires}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-491">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-492">
                Hired
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-493">
              <div className="h-full bg-gradient-to-r from-navy to-navy/90 w-full transition-all duration-500 ease-out" data-id="element-494" />
            </div>
            <div className="w-10 md:w-16 flex-shrink-0" data-id="element-495" />
          </div>

          {/* Launched */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-496">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-497">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-498">
                {estimatedLaunched}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-499">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-500">
                Launched
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-501">
              <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500 ease-out" style={{
              width: `${estimatedLaunched / estimatedHires * 100}%`
            }} data-id="element-502" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-503">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-504">
                70%
              </span>
            </div>
          </div>

          {/* Active 30d */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-505">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-506">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-507">
                {Math.round(estimatedActive30)}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-508">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-509">
                Active 30d
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-510">
              <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-500 ease-out" style={{
              width: `${estimatedActive30 / estimatedHires * 100}%`
            }} data-id="element-511" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-512">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-513">
                30%
              </span>
            </div>
          </div>

          {/* Active 90d+ */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-514">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-515">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-516">
                {Math.round(estimatedActive90)}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-517">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-518">
                Active 90d+
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-519">
              <div className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500 ease-out" style={{
              width: `${estimatedActive90 / estimatedHires * 100}%`
            }} data-id="element-520" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-521">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-522">
                80%
              </span>
            </div>
          </div>

          {/* Active 1 Year */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-523">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-524">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-525">
                {Math.round(estimatedActive1Year)}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-526">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-527">
                Active 1 Year
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-528">
              <div className="h-full bg-gradient-to-r from-purple-500 to-purple-400 transition-all duration-500 ease-out" style={{
              width: `${estimatedActive1Year / estimatedHires * 100}%`
            }} data-id="element-529" />
            </div>
            <div className="w-10 md:w-16 text-right flex-shrink-0" data-id="element-530">
              <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-531">
                30%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}